import { CalendarDate, getLocalTimeZone, today } from "@internationalized/date";
import { useControlledState } from "@react-stately/utils";
import { Calendar as CalendarIcon } from "@untitledui/icons";
import { useEffect, useState } from "react";
import { useDateFormatter } from "react-aria";
import type {
	DatePickerProps as AriaDatePickerProps,
	DateValue,
	PopoverProps,
} from "react-aria-components";
import {
	DatePicker as AriaDatePicker,
	Dialog as AriaDialog,
	Group as AriaGroup,
	Popover as AriaPopover,
} from "react-aria-components";
import { Button } from "@/components/base/buttons/button";
import { cx } from "@/utils/cx";
import { Calendar } from "./calendar";

const highlightedDates = [today(getLocalTimeZone())];

function formatDateDdMmYyyy(date: DateValue): string {
	const d = String(date.day).padStart(2, "0");
	const m = String(date.month).padStart(2, "0");
	const y = date.year;
	return `${d}/${m}/${y}`;
}

function formatDateToYyyyMmDd(date: DateValue | null): string {
	if (!date) {
		return "";
	}
	const y = date.year;
	const m = String(date.month).padStart(2, "0");
	const d = String(date.day).padStart(2, "0");
	return `${y}-${m}-${d}`;
}

function parseYyyyMmDdToDateValue(str: string): DateValue | null {
	if (!str) {
		return null;
	}
	const [y, m, d] = str.split("-").map(Number);
	if (Number.isNaN(y) || Number.isNaN(m) || Number.isNaN(d)) {
		return null;
	}
	return new CalendarDate(y, m, d);
}

function useIsMobileDevice(): boolean {
	const [isMobile, setIsMobile] = useState(false);

	useEffect(() => {
		const checkMobile = () => {
			const hasTouchScreen =
				"ontouchstart" in window || navigator.maxTouchPoints > 0;
			const isNarrowScreen = window.innerWidth < 640;
			setIsMobile(hasTouchScreen && isNarrowScreen);
		};
		checkMobile();
		window.addEventListener("resize", checkMobile);
		return () => window.removeEventListener("resize", checkMobile);
	}, []);

	return isMobile;
}

interface DatePickerProps extends AriaDatePickerProps<DateValue> {
	/** The function to call when the apply button is clicked. */
	onApply?: (date: DateValue | null) => void;
	/** The function to call when the cancel button is clicked. */
	onCancel?: () => void;
	/** Exibir data no formato dd/mm/yyyy no botão. Se false, usa o formato do locale. */
	displayFormatDdMmYyyy?: boolean;
	/** Posição do popover do calendário. Default: "bottom start". */
	popoverPlacement?: PopoverProps["placement"];
	/** Distância do popover em relação ao botão trigger. Default: 8. */
	popoverOffset?: number;
	/** Show the "Hoje" (Today) preset button in the calendar. Default: true. */
	showTodayButton?: boolean;
	triggerSize?: "sm" | "md";
}

export const DatePicker = ({
	value: valueProp,
	defaultValue,
	onChange,
	onApply,
	onCancel,
	displayFormatDdMmYyyy = false,
	popoverPlacement = "bottom start",
	popoverOffset = 8,
	showTodayButton = true,
	triggerSize = "md",
	...props
}: DatePickerProps) => {
	const isMobileDevice = useIsMobileDevice();
	const formatter = useDateFormatter({
		month: "short",
		day: "numeric",
		year: "numeric",
	});
	const [value, setValue] = useControlledState(
		valueProp,
		defaultValue || null,
		onChange
	);
	const [tempValue, setTempValue] = useState(value);

	useEffect(() => {
		setTempValue(value);
	}, [value]);

	const formattedDate = value
		? displayFormatDdMmYyyy
			? formatDateDdMmYyyy(value)
			: formatter.format(value.toDate(getLocalTimeZone()))
		: "Selecionar data";

	if (isMobileDevice) {
		return (
			<div className="relative">
				<Button
					className="pointer-events-none w-full justify-start"
					color="secondary"
					iconLeading={CalendarIcon}
					size={triggerSize}
				>
					{formattedDate}
				</Button>
				<input
					className="absolute inset-0 z-10 cursor-pointer opacity-0"
					onChange={(e) => {
						const newValue = parseYyyyMmDdToDateValue(e.target.value);
						setValue(newValue);
						onApply?.(newValue);
					}}
					type="date"
					value={formatDateToYyyyMmDd(value)}
				/>
			</div>
		);
	}

	return (
		<AriaDatePicker
			shouldCloseOnSelect={false}
			{...props}
			onChange={setTempValue}
			value={tempValue}
		>
			<AriaGroup>
				<Button color="secondary" iconLeading={CalendarIcon} size={triggerSize}>
					{formattedDate}
				</Button>
			</AriaGroup>
			<AriaPopover
				className={({ isEntering, isExiting }) =>
					cx(
						"origin-(--trigger-anchor-point) will-change-transform",
						"z-9999 max-h-[calc(100dvh-2rem)] overflow-y-auto",
						isEntering &&
							"fade-in placement-right:slide-in-from-left-0.5 placement-top:slide-in-from-bottom-0.5 placement-bottom:slide-in-from-top-0.5 animate-in duration-150 ease-out",
						isExiting &&
							"fade-out placement-right:slide-out-to-left-0.5 placement-top:slide-out-to-bottom-0.5 placement-bottom:slide-out-to-top-0.5 animate-out duration-100 ease-in"
					)
				}
				containerPadding={16}
				offset={popoverOffset}
				placement={popoverPlacement}
				shouldFlip
			>
				<AriaDialog
					className={cx(
						"rounded-2xl bg-primary shadow-xl ring ring-secondary_alt"
					)}
				>
					{({ close }) => (
						<>
							<div className="px-6 py-5">
								<Calendar
									highlightedDates={highlightedDates}
									onChange={setTempValue}
									showTodayButton={showTodayButton}
									value={tempValue}
								/>
							</div>
							<div className="grid grid-cols-2 gap-3 border-secondary border-t p-4">
								<Button
									color="secondary"
									onClick={() => {
										onCancel?.();
										close();
									}}
									size="md"
								>
									Cancelar
								</Button>
								<Button
									color="primary"
									onClick={() => {
										setValue(tempValue);
										onApply?.(tempValue);
										close();
									}}
									size="md"
								>
									Aplicar
								</Button>
							</div>
						</>
					)}
				</AriaDialog>
			</AriaPopover>
		</AriaDatePicker>
	);
};
